import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, ExternalLink, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import { diretoriaToneTextClass } from '@/lib/diretoriaKpiTone';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import {
  dashboardToAgingChart,
  dashboardToEmpresaBar,
  mergeExecutiveWithDashboard,
  type DiretoriaDashboardStats,
} from '@/lib/diretoriaDashboard';
import {
  DIRETORIA_DEMO_COMPANIES,
  DIRETORIA_DEMO_PERIODS,
  DIRETORIA_RIQUE_IA_URL,
  getDiretoriaExecutiveKpis,
  getDiretoriaIndicators,
  getDiretoriaOverviewAgingMock,
  getDiretoriaOverviewEvolucao,
  getDiretoriaOverviewUnidadesMock,
  type DiretoriaIndicatorMock,
} from '@/data/diretoriaMock';
import { DiretoriaModalChart } from '@/components/Diretoria/DiretoriaModalChart';
import { DiretoriaExecutiveStrip } from '@/components/Diretoria/DiretoriaExecutiveStrip';
import { DiretoriaOverviewSection } from '@/components/Diretoria/DiretoriaOverviewSection';
import { DiretoriaSparkline } from '@/components/Diretoria/DiretoriaSparkline';

export const DiretoriaPage: React.FC = () => {
  const { user } = useAuthStore();
  const [companyId, setCompanyId] = useState<string>(DIRETORIA_DEMO_COMPANIES[0].id);
  const [periodId, setPeriodId] = useState<string>(DIRETORIA_DEMO_PERIODS[0].id);
  const [billingCompanyId, setBillingCompanyId] = useState<string>('');
  const [open, setOpen] = useState<DiretoriaIndicatorMock | null>(null);

  const { data: companies } = useQuery({
    queryKey: ['diretoria-companies', user?.id, user?.role],
    queryFn: async () => {
      const response = await api.get('/companies?page=1&limit=500');
      const payload = response?.data?.data ?? response?.data ?? [];
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.companies)) return payload.companies;
      if (Array.isArray(payload?.items)) return payload.items;
      return [];
    },
  });

  const {
    data: dashboardStats,
    isLoading: dashboardLoading,
    isError: dashboardError,
  } = useQuery({
    queryKey: ['diretoria-dashboard', billingCompanyId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (billingCompanyId) params.append('companyId', billingCompanyId);
      const response = await api.get(`/reports/dashboard?${params.toString()}`);
      return response.data.data as DiretoriaDashboardStats;
    },
    retry: 1,
    staleTime: 30_000,
  });

  const demoSubtitle = useMemo(() => {
    const c = DIRETORIA_DEMO_COMPANIES.find((x) => x.id === companyId)?.label ?? '';
    const p = DIRETORIA_DEMO_PERIODS.find((x) => x.id === periodId)?.label ?? '';
    return `${c} · ${p}`;
  }, [companyId, periodId]);

  const indicators = useMemo(() => getDiretoriaIndicators(companyId, periodId), [companyId, periodId]);

  const dashboardReady = Boolean(dashboardStats) && !dashboardError && !dashboardLoading;

  const executiveCards = useMemo(() => {
    const base = getDiretoriaExecutiveKpis(companyId, periodId);
    if (!dashboardReady || !dashboardStats) return base;
    return mergeExecutiveWithDashboard(base, dashboardStats);
  }, [companyId, periodId, dashboardReady, dashboardStats]);

  const evolucao = useMemo(() => getDiretoriaOverviewEvolucao(companyId, periodId), [companyId, periodId]);

  const agingChart = useMemo(() => {
    if (dashboardReady && dashboardStats) {
      const fromApi = dashboardToAgingChart(dashboardStats);
      if (fromApi) return fromApi;
    }
    return getDiretoriaOverviewAgingMock(companyId, periodId);
  }, [companyId, periodId, dashboardReady, dashboardStats]);

  const empresasChart = useMemo(() => {
    if (dashboardReady && dashboardStats) {
      const fromApi = dashboardToEmpresaBar(dashboardStats, 8);
      if (fromApi?.length) return fromApi;
    }
    return getDiretoriaOverviewUnidadesMock(companyId, periodId);
  }, [companyId, periodId, dashboardReady, dashboardStats]);

  const agingSubtitle = dashboardReady ? 'Saldo em R$ e quantidade de títulos (dashboard)' : 'Saldo em R$ (demonstração)';
  const empresasSubtitle = dashboardReady ? 'Saldo Recebido em R$ por empresa (dashboard)' : 'Saldo em R$ (demonstração)';

  return (
    <>
      <Helmet>
        <title>Diretoria — PortalFly</title>
      </Helmet>

      <div className="mx-auto w-full max-w-[min(100%,1680px)] space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="mb-1 flex items-center gap-2 text-muted-foreground">
              <BarChart3 className="h-5 w-5" aria-hidden />
              <span className="text-xs font-medium uppercase tracking-wide">Módulo</span>
            </div>
            <h1 className="font-heading text-heading-2 font-bold text-foreground">Diretoria</h1>
            
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              Cenário frota (demo)
              <select
                className={cn(
                  'h-9 min-w-[12rem] rounded-md border border-input bg-background px-2 text-sm text-foreground',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                )}
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
              >
                {DIRETORIA_DEMO_COMPANIES.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              Período (demo)
              <select
                className={cn(
                  'h-9 min-w-[10rem] rounded-md border border-input bg-background px-2 text-sm text-foreground',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                )}
                value={periodId}
                onChange={(e) => setPeriodId(e.target.value)}
              >
                {DIRETORIA_DEMO_PERIODS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              Cobrança (dados reais)
              <select
                className={cn(
                  'h-9 min-w-[12rem] rounded-md border border-input bg-background px-2 text-sm text-foreground',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                )}
                value={billingCompanyId}
                onChange={(e) => setBillingCompanyId(e.target.value)}
              >
                <option value="">Todas no escopo</option>
                {(Array.isArray(companies) ? companies : []).map((company: { id: string; name: string }) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <p className="text-xs text-muted-foreground">
            Cenário frota selecionado: <span className="font-medium text-foreground">{demoSubtitle}</span>
            {billingCompanyId ? (
              <>
                {' '}
                · Cobrança filtrada por empresa no dashboard
              </>
            ) : null}
          </p>
          
        </div>

        <DiretoriaExecutiveStrip cards={executiveCards} />

        <DiretoriaOverviewSection
          evolucao={evolucao}
          aging={agingChart}
          empresas={empresasChart}
          agingSubtitle={agingSubtitle}
          empresasSubtitle={empresasSubtitle}
        />

        <div>
          <h2 className="mb-3 font-heading text-lg font-semibold text-foreground">Indicadores</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {indicators.map((ind) => {
              const pk = ind.primaryKpiIndex ?? 0;
              const primary = ind.kpis[pk];
              const secondary = ind.kpis.filter((_, i) => i !== pk);
              return (
                <button
                  key={ind.id}
                  type="button"
                  onClick={() => setOpen(ind)}
                  className="text-left transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  <Card className="h-full border-border/80 hover:shadow-card-hover">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base leading-snug">{ind.title}</CardTitle>
                      <p className="text-xs text-muted-foreground line-clamp-2">{ind.shortDescription}</p>
                    </CardHeader>
                    <CardContent className="space-y-3 pt-0">
                      {primary ? (
                        <div>
                          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{primary.label}</p>
                          <p
                            className={cn(
                              'font-heading text-2xl font-bold tabular-nums',
                              diretoriaToneTextClass(primary.tone)
                            )}
                          >
                            {primary.value}
                          </p>
                        </div>
                      ) : null}
                      <DiretoriaSparkline indicator={ind} />
                      {secondary.map((k) => (
                        <div
                          key={k.label}
                          className="flex items-baseline justify-between gap-2 border-t border-border/60 pt-2 first:border-t-0 first:pt-0"
                        >
                          <span className="text-xs text-muted-foreground">{k.label}</span>
                          <span className={cn('text-sm font-semibold', diretoriaToneTextClass(k.tone))}>{k.value}</span>
                        </div>
                      ))}
                      <span className="inline-block pt-1 text-xs font-medium text-amber-700 dark:text-amber-400">Ver análise</span>
                    </CardContent>
                  </Card>
                </button>
              );
            })}
            <Card
              className={cn(
                'relative flex h-full flex-col overflow-hidden border-border/80 border-dashed bg-gradient-to-br from-primary/[0.07] via-background to-muted/50',
                'shadow-soft-sm transition-shadow duration-200 hover:shadow-card-hover'
              )}
            >
              <div
                className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-primary/[0.12] blur-2xl"
                aria-hidden
              />
              <CardHeader className="relative space-y-0 pb-3 pt-1">
                <div className="flex items-start gap-3">
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary shadow-inner"
                    aria-hidden
                  >
                    <Sparkles className="h-5 w-5" strokeWidth={2} />
                  </div>
                  <div className="min-w-0 space-y-0.5 pt-0.5">
                    <CardTitle className="text-base leading-tight">Rique.Ia</CardTitle>
                    <p className="text-xs font-medium text-muted-foreground">Assistente CorpsTek</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="relative flex flex-1 flex-col gap-4 pt-0">
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Dúvidas? Entre em contato com o seu assistente,{' '}
                  <span className="font-semibold text-foreground">Rique.Ia</span>.
                </p>
                <div className="mt-auto border-t border-border/60 pt-3">
                  <a
                    href={DIRETORIA_RIQUE_IA_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'inline-flex h-9 w-full items-center justify-center gap-2 rounded-button px-4 text-sm font-medium',
                      'bg-primary text-primary-foreground shadow-button',
                      'hover:bg-primary/90 hover:shadow-button-hover hover:-translate-y-0.5',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      'sm:w-auto sm:justify-start'
                    )}
                  >
                    Clique aqui
                    <ExternalLink className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Modal isOpen={Boolean(open)} onClose={() => setOpen(null)} title={open?.title ?? 'Indicador'} size="xl">
        {open ? (
          <div className="space-y-4">
            <DiretoriaModalChart indicator={open} height={open.chartKind === 'pie' ? 320 : 260} />
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50 text-left">
                    {open.tableHeaders.map((h) => (
                      <th key={h} className="px-3 py-2 font-medium">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {open.tableRows.map((row, ri) => (
                    <tr key={ri} className="border-b border-border/80 last:border-0">
                      {row.map((cell, ci) => (
                        <td key={ci} className="px-3 py-2 text-foreground">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">{open.footnote}</p>
            {open.id === 'ia-zeh' ? (
              <div className="flex flex-wrap gap-2 border-t border-border pt-3">
                <Link
                  to="/whatsapp-integration"
                  className={cn(
                    'inline-flex h-8 items-center justify-center rounded-button border border-input bg-background px-3 text-body-sm font-medium shadow-soft-sm',
                    'hover:bg-muted hover:border-input hover:shadow-soft-md'
                  )}
                >
                  Abrir integrações WhatsApp
                </Link>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </>
  );
};
